import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Form,
  Input,
  Label,
  Button,
  FormFeedback,
  Row,
  Col,
} from "reactstrap";
import { FaTimes } from "react-icons/fa";
import { getCountryList } from "actions/country";

const BankDetailModal = ({
  isOpen,
  toggleModal,
  onSubmit,
  billingData = {},
  onBillingChange,
  onBillingComplete,
  legalEntityName,
}) => {
  // Billing form state
  const [billingForm, setBillingForm] = useState({
    legal_entity_name: "",
    billing_address: "",
    country: "",
    trn_vat_id: "",
    tax_residency: "",
    payment_currency: "",
    contact_email: "",
    recipient_name: "",
    bank_name: "",
    recipient_address: "",
    bank_address: "",
    bank_account: "",
    iban: "",
    swiftCode: "",
    routing_number: "",
    ifscCode: "",
  });

  const [billingErrors, setBillingErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [countries, setCountries] = useState([]);

  const data = JSON.parse(localStorage.getItem("p"));
  const companyName = data?.name;
  const userRole = data?.role;

  // Currencies list
  const currencies = [
    { value: 1, label: "USD - US Dollar" },
    { value: 2, label: "AED - UAE Dirham" },
    { value: 3, label: "EUR - Euro" },
    { value: 4, label: "GBP - British Pound" },
    { value: 5, label: "SGD - Singapore Dollar" },
    { value: 6, label: "INR - Indian Rupee" },
    { value: 7, label: "AUD - Australian Dollar" },
    { value: 8, label: "CAD - Canadian Dollar" },
    { value: 9, label: "JPY - Japanese Yen" },
    { value: 10, label: "CNY - Chinese Yuan" },
    { value: 11, label: "HKD - Hong Kong Dollar" },
    { value: 12, label: "BRL - Brazilian Real" },
    { value: 13, label: "ZAR - South African Rand" },
    { value: 14, label: "NGN - Nigerian Naira" },
    { value: 15, label: "KES - Kenyan Shilling" },
    { value: 16, label: "EGP - Egyptian Pound" },
    { value: 17, label: "MYR - Malaysian Ringgit" },
    { value: 18, label: "IDR - Indonesian Rupiah" },
    { value: 19, label: "PHP - Philippine Peso" },
    { value: 20, label: "THB - Thai Baht" },
    { value: 21, label: "VND - Vietnamese Dong" },
    { value: 22, label: "TWD - Taiwan Dollar" },
    { value: 23, label: "KRW - South Korean Won" },
    { value: 24, label: "NZD - New Zealand Dollar" },
  ];

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBillingForm({
        legal_entity_name:
          userRole === "SUPER_ADMIN" ? legalEntityName : companyName,
        billing_address: "",
        country: "",
        trn_vat_id: "",
        tax_residency: "",
        payment_currency: "",
        contact_email: "",
        recipient_name: "",
        bank_name: "",
        recipient_address: "",
        bank_address: "",
        bank_account: "",
        iban: "",
        swiftCode: "",
        routing_number: "",
        ifscCode: "",
      });
      setBillingErrors({});
      setApiError("");
      setLoading(false);
    }
  }, [isOpen, companyName, legalEntityName, userRole]);

  // Fetch countries when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCountries();
    }
  }, [isOpen]);

  const fetchCountries = async () => {
    try {
      const res = await getCountryList();

      let countryData = [];

      if (res?.code === 200) {
        if (res.data) {
          if (Array.isArray(res.data)) {
            countryData = res.data;
          } else if (res.data.Country) {
            countryData = res.data.Country;
          } else if (res.data.Publisher) {
            countryData = res.data.Publisher;
          } else {
            const values = Object.values(res.data);
            if (values.length > 0 && Array.isArray(values[0])) {
              countryData = values[0];
            }
          }
        }
      }

      setCountries(countryData);
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      setCountries([]);
    }
  };

  // Populate billing data when provided
  useEffect(() => {
    if (billingData && Object.keys(billingData).length > 0) {
      setBillingForm((prev) => ({
        ...prev,
        legal_entity_name:
          billingData.legalEntityName ||
          billingData.legal_entity_name ||
          billingData.companyName ||
          "",
        billing_address:
          billingData.billingAddress ||
          billingData.billing_address ||
          billingData.address ||
          "",
        country: billingData.country || "",
        trn_vat_id:
          billingData.trnVatId ||
          billingData.trn_vat_id ||
          billingData.trn ||
          "",
        tax_residency:
          billingData.taxResidency ||
          billingData.tax_residency ||
          "",
        payment_currency:
          billingData.paymentCurrency ||
          billingData.payment_currency ||
          "",
        contact_email:
          billingData.contactEmail ||
          billingData.contact_email ||
          billingData.email ||
          "",
        recipient_name:
          billingData.recipientName ||
          billingData.recipient_name ||
          billingData.companyName ||
          "",
        bank_name: billingData.bankName || billingData.bank_name || "",
        recipient_address:
          billingData.recipientAddress ||
          billingData.recipient_address ||
          billingData.address ||
          "",
        bank_account: billingData.bankAccount || billingData.bank_account || "",
        bank_address: billingData.bankAddress || billingData.bank_address || "",
        iban: billingData.iban || "",
        swiftCode: billingData.swiftBic || billingData.swiftCode || "",
        routing_number:
          billingData.routingNumber || billingData.routing_number || "",
        ifscCode: billingData.ifscCode || billingData.ifsc_code || "",
      }));
    }
  }, [billingData]);

  // Billing field change handler
  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingForm((prev) => ({ ...prev, [name]: value }));
    if (billingErrors[name]) {
      setBillingErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (onBillingChange) {
      onBillingChange({ ...billingForm, [name]: value });
    }
  };

  // Handle Tax Residency selection - stores full country label (e.g., "India (IN)")
  const handleTaxResidencyChange = (e) => {
    const countryLabel = e.target.value;

    setBillingForm((prev) => ({
      ...prev,
      tax_residency: countryLabel,
    }));

    if (billingErrors.tax_residency) {
      setBillingErrors((prev) => ({ ...prev, tax_residency: "" }));
    }

    if (onBillingChange) {
      onBillingChange({ ...billingForm, tax_residency: countryLabel });
    }
  };

  // Handle Payment Currency selection - stores full currency label (e.g., "USD - US Dollar")
  const handleCurrency = (e) => {
    const currencyLabel = e.target.value;

    setBillingForm((prev) => ({
      ...prev,
      payment_currency: currencyLabel,
    }));

    if (billingErrors.payment_currency) {
      setBillingErrors((prev) => ({ ...prev, payment_currency: "" }));
    }

    if (onBillingChange) {
      onBillingChange({ ...billingForm, payment_currency: currencyLabel });
    }
  };

  // Get country label with code
  const getCountryLabel = (country) => {
    if (country.countryName && country.countryCode) {
      return `${country.countryName} (${country.countryCode})`;
    }
    if (country.country_name && country.country_code) {
      return `${country.country_name} (${country.country_code})`;
    }
    if (country.name && country.code) {
      return `${country.name} (${country.code})`;
    }
    return country.countryName || country.country_name || country.name || "";
  };

  // Get country ID
  const getCountryId = (country) => {
    return country.ID || country.id || country.Id || "";
  };

  // Validate billing
  const validateBilling = () => {
    let err = {};

    if (!billingForm.legal_entity_name)
      err.legal_entity_name = "Legal entity name is required";
    if (!billingForm.billing_address)
      err.billing_address = "Billing address is required";
    if (!billingForm.country) err.country = "Country is required";
    if (!billingForm.tax_residency)
      err.tax_residency = "Tax residency is required";
    if (!billingForm.payment_currency)
      err.payment_currency = "Payment currency is required";
    if (!billingForm.contact_email)
      err.contact_email = "Contact email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingForm.contact_email))
      err.contact_email = "Invalid email format";

    if (!billingForm.bank_name) err.bank_name = "Bank name is required";
    if (!billingForm.bank_account) err.bank_account = "Bank account is required";
    if (!billingForm.iban) err.iban = "IBAN is required";
    if (!billingForm.swiftCode) err.swiftCode = "SWIFT/BIC is required";
    if (!billingForm.ifscCode) err.ifscCode = "IFSC Code is required";

    setBillingErrors(err);
    return Object.keys(err).length === 0;
  };

  // Handle billing submission
  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (!validateBilling()) return;

    try {
      setLoading(true);

      const payload = {
        billingInfo: {
          legalEntityName: billingForm.legal_entity_name,
          billingAddress: billingForm.billing_address,
          country: billingForm.country,
          trnVatId: billingForm.trn_vat_id,
          taxResidency: billingForm.tax_residency, // full label e.g. "France (FR)"
          paymentCurrency: billingForm.payment_currency, // full label e.g. "INR - Indian Rupee"
          contactEmail: billingForm.contact_email,
          recipientName: billingForm.recipient_name,
          bankName: billingForm.bank_name,
          recipientAddress: billingForm.recipient_address,
          bankAccount: billingForm.bank_account,
          bankAddress: billingForm.bank_address,
          iban: billingForm.iban,
          swiftBic: billingForm.swiftCode,
          routingNumber: billingForm.routing_number,
          ifscCode: billingForm.ifscCode,
        },
      };

      const res = await onSubmit(payload);

      if (res?.code !== 200) {
        setApiError(res?.message || "Failed to save billing information");
        setLoading(false);
        return;
      }

      setLoading(false);
      setApiError("");

      if (onBillingComplete) onBillingComplete(billingForm);
      if (toggleModal) toggleModal();
    } catch (err) {
      setApiError("Something went wrong");
      setLoading(false);
    }
  };

  // Close modal handler
  const handleClose = () => {
    if (toggleModal) {
      toggleModal();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      backdrop="static"
      keyboard={false}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalHeader toggle={handleClose}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>Add New Bank Details</span>
        </div>
      </ModalHeader>

      <Form onSubmit={handleBillingSubmit}>
        <ModalBody>
          {/* Legal Entity Name / Country */}
          <Row>
            <Col md={6}>
              <Label>
                Legal Entity Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="legal_entity_name"
                autoComplete="off"
                disabled={true}
                value={billingForm.legal_entity_name}
                onChange={handleBillingChange}
                invalid={!!billingErrors.legal_entity_name}
                placeholder="Enter legal entity name"
              />
              <FormFeedback>{billingErrors.legal_entity_name}</FormFeedback>
            </Col>
            <Col md={6}>
              <Label>
                Country <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                name="country"
                autoComplete="off"
                value={billingForm.country}
                onChange={handleBillingChange}
                invalid={!!billingErrors.country}
              >
                <option value="">Select Country</option>
                {countries && countries.length > 0 ? (
                  countries.map((c) => (
                    <option key={getCountryId(c)} value={getCountryId(c)}>
                      {getCountryLabel(c)}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No countries available
                  </option>
                )}
              </Input>
              <FormFeedback>{billingErrors.country}</FormFeedback>
            </Col>
          </Row>

          {/* Billing Address */}
          <Row className="mt-2">
            <Col md={12}>
              <Label>
                Billing Address <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="billing_address"
                autoComplete="off"
                value={billingForm.billing_address}
                onChange={handleBillingChange}
                invalid={!!billingErrors.billing_address}
                placeholder="Enter billing address"
              />
              <FormFeedback>{billingErrors.billing_address}</FormFeedback>
            </Col>
          </Row>

          {/* TRN/VAT ID & Tax Residency */}
          <Row className="mt-2">
            <Col md={6}>
              <Label>
                TRN/VAT ID <span className="text-muted">(if applicable)</span>
              </Label>
              <Input
                type="text"
                name="trn_vat_id"
                autoComplete="off"
                value={billingForm.trn_vat_id}
                onChange={handleBillingChange}
                invalid={!!billingErrors.trn_vat_id}
                placeholder="Enter TRN or VAT ID"
              />
              <FormFeedback>{billingErrors.trn_vat_id}</FormFeedback>
            </Col>
            <Col md={6}>
              <Label>
                Tax Residency <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                name="tax_residency"
                autoComplete="off"
                value={billingForm.tax_residency}
                onChange={handleTaxResidencyChange}
                invalid={!!billingErrors.tax_residency}
              >
                <option value="">Select Tax Residency</option>
                {countries && countries.length > 0 ? (
                  countries.map((c) => {
                    const label = getCountryLabel(c);
                    return (
                      <option key={getCountryId(c)} value={label}>
                        {label}
                      </option>
                    );
                  })
                ) : (
                  <option value="" disabled>
                    No countries available
                  </option>
                )}
              </Input>
              <FormFeedback>{billingErrors.tax_residency}</FormFeedback>
            </Col>
          </Row>

          {/* Payment Currency & Contact Email */}
          <Row className="mt-2">
            <Col md={6}>
              <Label>
                Payment Currency <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                name="payment_currency"
                autoComplete="off"
                value={billingForm.payment_currency}
                onChange={handleCurrency}
                invalid={!!billingErrors.payment_currency}
              >
                <option value="">Select Currency</option>
                {currencies.map((c) => (
                  <option key={c.value} value={c.label}>
                    {c.label}
                  </option>
                ))}
              </Input>
              <FormFeedback>{billingErrors.payment_currency}</FormFeedback>
            </Col>
            <Col md={6}>
              <Label>
                Contact Email for Invoices{" "}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="email"
                name="contact_email"
                autoComplete="off"
                value={billingForm.contact_email}
                onChange={handleBillingChange}
                invalid={!!billingErrors.contact_email}
                placeholder="Enter contact email"
              />
              <FormFeedback>{billingErrors.contact_email}</FormFeedback>
            </Col>
          </Row>

          {/* Bank Name & Bank Account */}
          <Row className="mt-2">
            <Col md={6}>
              <Label>
                Bank Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="bank_name"
                autoComplete="off"
                value={billingForm.bank_name}
                onChange={handleBillingChange}
                invalid={!!billingErrors.bank_name}
                placeholder="Enter bank name"
              />
              <FormFeedback>{billingErrors.bank_name}</FormFeedback>
            </Col>
            <Col md={6}>
              <Label>
                Bank Account <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                autoComplete="off"
                name="bank_account"
                value={billingForm.bank_account}
                onChange={handleBillingChange}
                invalid={!!billingErrors.bank_account}
                placeholder="Enter bank account"
              />
              <FormFeedback>{billingErrors.bank_account}</FormFeedback>
            </Col>
          </Row>

          {/* IBAN & SWIFT/BIC */}
          <Row className="mt-2">
            <Col md={6}>
              <Label>
                IBAN <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="iban"
                autoComplete="off"
                value={billingForm.iban}
                onChange={handleBillingChange}
                invalid={!!billingErrors.iban}
                placeholder="Enter IBAN"
              />
              <FormFeedback>{billingErrors.iban}</FormFeedback>
            </Col>
            <Col md={6}>
              <Label>
                SWIFT Code <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="swiftCode"
                autoComplete="off"
                value={billingForm.swiftCode}
                onChange={handleBillingChange}
                invalid={!!billingErrors.swiftCode}
                placeholder="Enter SWIFT/BIC"
              />
              <FormFeedback>{billingErrors.swiftCode}</FormFeedback>
            </Col>
          </Row>

          {/* IFSC Code */}
          <Row className="mt-2">
            <Col md={6}>
              <Label>
                IFSC Code <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="ifscCode"
                autoComplete="off"
                value={billingForm.ifscCode}
                onChange={handleBillingChange}
                invalid={!!billingErrors.ifscCode}
                placeholder="Enter IFSC Code"
              />
              <FormFeedback>{billingErrors.ifscCode}</FormFeedback>
            </Col>
          </Row>
          

          {/* Note about admin approval */}
          <Row className="mt-3">
            <Col md={12}>
              <div className="text-muted small">
                <i className="fa fa-info-circle me-1"></i>
                Your billing information will be reviewed and approved by the
                admin. The dashboard will be unlocked upon approval.
              </div>
            </Col>
          </Row>

          {apiError && <div className="text-danger mt-3">{apiError}</div>}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              className="btn-gradient-primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Saving..." : "Add Bank Details"}
            </Button>
          </div>
        </ModalBody>
      </Form>
    </Modal>
  );
};

export default BankDetailModal;